import { Box, Typography, lighten, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CallJourneyStage } from './types';

type Props = {
  stages: CallJourneyStage[];
};

const PLOT_HEIGHT = 280;
const LABEL_HEIGHT = 56;
const TOP_PAD = 4;
const Y_AXIS_WIDTH = 44;
const RIGHT_PAD = 4;
const TICK_COUNT = 4;
const GRID = '#E5E7EB';

/**
 * Traced from design: hold stage height ~35%, S-curve to next ~30%,
 * then hold next height until the stage edge (~35%).
 */
const FLAT_START = 0.35;
const CURVE_END = 0.65;

/** Progressive lighten amounts from theme primary (left → right). */
const FUNNEL_LIGHTEN = [0, 0.2, 0.4, 0.55];

function niceMax(maxValue: number): number {
  if (maxValue <= 0) return 100;
  const padded = maxValue * 1.08;
  const rawStep = padded / TICK_COUNT;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const steps = [1, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 7, 7.5, 8, 10];
  const nice = steps.find((n) => n >= normalized) ?? 10;
  let max = nice * magnitude * TICK_COUNT;
  if (max < maxValue) {
    const next = steps.find((n) => n * magnitude * TICK_COUNT >= maxValue) ?? nice * 2;
    max = next * magnitude * TICK_COUNT;
  }
  return max;
}

function formatCount(n: number) {
  return n.toLocaleString('en-US');
}

/**
 * Design ribbon segment: flat → smooth S-curve → flat (no sharp corners).
 * Last stage is a constant-height bar.
 */
function buildSegmentPath(
  x0: number,
  x1: number,
  top0: number,
  bottom0: number,
  top1: number,
  bottom1: number,
  isLast: boolean
): string {
  if (isLast || (top0 === top1 && bottom0 === bottom1)) {
    return `M ${x0} ${top0} L ${x1} ${top0} L ${x1} ${bottom0} L ${x0} ${bottom0} Z`;
  }

  const w = x1 - x0;
  const xFlatEnd = x0 + w * FLAT_START;
  const xCurveEnd = x0 + w * CURVE_END;
  const cx = (xFlatEnd + xCurveEnd) / 2;

  return [
    `M ${x0} ${top0}`,
    // flat at current stage height
    `L ${xFlatEnd} ${top0}`,
    // smooth S-curve into next stage height (horizontal end tangents)
    `C ${cx} ${top0}, ${cx} ${top1}, ${xCurveEnd} ${top1}`,
    // flat at next stage height until boundary
    `L ${x1} ${top1}`,
    `L ${x1} ${bottom1}`,
    `L ${xCurveEnd} ${bottom1}`,
    `C ${cx} ${bottom1}, ${cx} ${bottom0}, ${xFlatEnd} ${bottom0}`,
    `L ${x0} ${bottom0}`,
    'Z'
  ].join(' ');
}

export default function CallJourneyFunnel({ stages }: Props) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const chart = useMemo(() => {
    const values = stages.map((s) => s.value);
    const maxValue = niceMax(Math.max(...values, 0));
    const ticks = Array.from({ length: TICK_COUNT + 1 }, (_, i) => Math.round((maxValue / TICK_COUNT) * i));

    const plotWidth = Math.max(width - Y_AXIS_WIDTH - RIGHT_PAD, 200);
    const n = Math.max(stages.length, 1);
    const stageWidth = plotWidth / n;
    const plotTop = TOP_PAD;
    const plotBottom = TOP_PAD + PLOT_HEIGHT;
    const center = plotTop + PLOT_HEIGHT / 2;

    const half = (v: number) => ((v / maxValue) * PLOT_HEIGHT) / 2;
    const overlap = 0.5;

    const ribbons = stages.map((stage, i) => {
      const isLast = i === n - 1;
      const next = isLast ? stage : stages[i + 1];
      const x0 = i * stageWidth - (i === 0 ? 0 : overlap / 2);
      const x1 = (i + 1) * stageWidth + (isLast ? 0 : overlap / 2);
      const h0 = half(stage.value);
      const h1 = half(next.value);
      const lightenAmount = FUNNEL_LIGHTEN[i] ?? FUNNEL_LIGHTEN[FUNNEL_LIGHTEN.length - 1];
      return {
        key: stage.key,
        color: lightenAmount === 0 ? primaryColor : lighten(primaryColor, lightenAmount),
        path: buildSegmentPath(x0, x1, center - h0, center + h0, center - h1, center + h1, isLast)
      };
    });

    const labels = stages.map((stage, i) => ({
      key: stage.key,
      label: stage.label,
      value: stage.value,
      midX: i * stageWidth + stageWidth / 2
    }));

    const tickYs = ticks.map((tick) => ({
      tick,
      y: plotBottom - (tick / maxValue) * PLOT_HEIGHT
    }));

    const verticalLines = Array.from({ length: n + 1 }, (_, i) => i * stageWidth);

    return {
      plotTop,
      plotBottom,
      totalHeight: TOP_PAD + PLOT_HEIGHT + LABEL_HEIGHT,
      ribbons,
      labels,
      tickYs,
      verticalLines
    };
  }, [stages, width, primaryColor]);

  return (
    <Box bgcolor='#fff' border='1px solid #E5E7EB' borderRadius='12px' p={3}>
      <Typography fontSize={20} fontWeight={600} color='#101828'>
        Outbound Call Journey
      </Typography>
      <Typography fontSize={14} color='#667085' mb={2.5} mt={0.25}>
        Track calls from dialing to final outcomes
      </Typography>

      <Box ref={containerRef} width='100%'>
        <svg
          width={width}
          height={chart.totalHeight}
          role='img'
          aria-label='Outbound call journey funnel'
          style={{ display: 'block', overflow: 'visible' }}
        >
          {chart.tickYs.map(({ tick, y }) => (
            <text
              key={`y-${tick}`}
              x={Y_AXIS_WIDTH - 10}
              y={y}
              textAnchor='end'
              dominantBaseline='middle'
              fill='#98A2B3'
              fontSize={12}
              fontFamily='Inter, system-ui, sans-serif'
            >
              {tick}
            </text>
          ))}

          <g transform={`translate(${Y_AXIS_WIDTH}, 0)`}>
            {chart.verticalLines.map((x, i) => (
              <line
                key={`v-${i}`}
                x1={x}
                x2={x}
                y1={chart.plotTop}
                y2={chart.plotBottom}
                stroke={GRID}
                strokeWidth={1}
              />
            ))}

            {chart.ribbons.map((ribbon) => (
              <path key={ribbon.key} d={ribbon.path} fill={ribbon.color} />
            ))}

            {chart.labels.map((item) => (
              <g key={`label-${item.key}`}>
                <text
                  x={item.midX}
                  y={chart.plotBottom + 24}
                  textAnchor='middle'
                  fill='#101828'
                  fontSize={15}
                  fontWeight={600}
                  fontFamily='Inter, system-ui, sans-serif'
                >
                  {formatCount(item.value)}
                </text>
                <text
                  x={item.midX}
                  y={chart.plotBottom + 44}
                  textAnchor='middle'
                  fill='#667085'
                  fontSize={13}
                  fontFamily='Inter, system-ui, sans-serif'
                >
                  {item.label}
                </text>
              </g>
            ))}
          </g>
        </svg>
      </Box>
    </Box>
  );
}
